import { useEffect, useMemo, useState } from "react";
import { Sparkles, Wand2, ListChecks, Loader2, Check, X, Pencil, ArrowRight, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * V14 — Panneau assistant IA pour une fiche athlète.
 *
 * Principe :
 *   1. À l'ouverture → GET /api/ai/athlete/:id/analyze (que peut-on améliorer ?)
 *   2. L'utilisateur choisit une action (bio / palmarès)
 *   3. L'IA propose → l'utilisateur voit **avant** + **après** → accepte, modifie ou ignore
 *   4. Aucun écrasement silencieux : chaque changement passe par PATCH explicite
 */
export function AIAssistantPanel({ athleteId, athlete, onApplied }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const loadAnalysis = () => {
    setLoading(true);
    api.get(`/ai/athlete/${athleteId}/analyze`)
      .then((r) => setAnalysis(r.data))
      .catch(() => setAnalysis(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (athleteId) loadAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  const applyPatch = async (payload) => {
    try {
      await api.patch(`/athletes/${athleteId}`, payload);
      toast.success("Modifications enregistrées.");
      onApplied?.();
      loadAnalysis();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-assistant-panel">
      <Card className="p-4 bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/10 to-transparent border-[hsl(var(--aito-lagoon))]/20">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[hsl(var(--aito-lagoon))]/20 p-2 shrink-0">
            <Sparkles className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-base">Assistant IA — accompagne ta fiche</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              L'IA analyse ce que tu as déjà, propose des améliorations et te laisse toujours valider.
              <strong className="text-foreground"> Aucun écrasement silencieux.</strong>
            </p>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="ai-tab-overview">
            <ListChecks className="h-4 w-4 mr-1.5" /> Que peut faire l'IA ?
          </TabsTrigger>
          <TabsTrigger value="bio" data-testid="ai-tab-bio">
            <Wand2 className="h-4 w-4 mr-1.5" /> Ma bio
          </TabsTrigger>
          <TabsTrigger value="palmares" data-testid="ai-tab-palmares">
            <Sparkles className="h-4 w-4 mr-1.5" /> Mon palmarès
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <AnalysisOverview analysis={analysis} loading={loading} onGoTo={setTab} />
        </TabsContent>

        <TabsContent value="bio" className="mt-4">
          <BioSuggester athleteId={athleteId} athlete={athlete} onApply={applyPatch} />
        </TabsContent>

        <TabsContent value="palmares" className="mt-4">
          <PalmaresSuggester athleteId={athleteId} athlete={athlete} onApply={applyPatch} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview : liste des suggestions détectées
// ---------------------------------------------------------------------------
function AnalysisOverview({ analysis, loading, onGoTo }) {
  if (loading) {
    return <Card className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Analyse de ta fiche…</Card>;
  }
  if (!analysis) {
    return <Card className="p-6 text-sm text-muted-foreground italic">Analyse IA indisponible pour le moment.</Card>;
  }
  const impactColor = { eleve: "bg-orange-500/10 text-orange-700 border-orange-500/30", moyen: "bg-yellow-500/10 text-yellow-800 border-yellow-500/30", faible: "bg-muted text-muted-foreground border-border" };
  const impactLabel = { eleve: "Impact élevé", moyen: "Impact moyen", faible: "Bonus" };
  return (
    <div className="space-y-3">
      <Card className="p-4" data-testid="ai-analysis-stats">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">État actuel de la fiche</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <StatMini label="Résultats" value={analysis.athlete.achievements_count} />
          <StatMini label="Articles" value={analysis.athlete.articles_count} />
          <StatMini label="Coachs" value={analysis.athlete.coaches_count} />
          <StatMini label="Bio longue" value={analysis.athlete.has_bio_long ? "✓" : "—"} />
        </div>
      </Card>

      {(analysis.suggestions || []).length === 0 ? (
        <Card className="p-6 text-center">
          <Check className="h-8 w-8 text-[hsl(var(--aito-lagoon))] mx-auto mb-2" />
          <p className="text-sm">Ta fiche est déjà bien remplie ! L'IA reste disponible si tu veux enrichir.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {analysis.suggestions.map((s) => (
            <Card key={s.key} className={`p-3 border ${impactColor[s.impact] || impactColor.faible}`} data-testid={`ai-suggestion-${s.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <Badge variant="outline" className="text-[9px]">{impactLabel[s.impact] || "Bonus"}</Badge>
                  </div>
                  <p className="text-xs">{s.desc}</p>
                </div>
                {(s.action === "bio-suggest" || s.action === "palmares-suggest") && (
                  <Button size="sm" onClick={() => onGoTo(s.action === "bio-suggest" ? "bio" : "palmares")} className="shrink-0">
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded-md border p-2 bg-background/60">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono font-semibold">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bio suggester : diff visuel + accept / modify / ignore
// ---------------------------------------------------------------------------
function BioSuggester({ athleteId, athlete, onApply }) {
  const [hints, setHints] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedShort, setEditedShort] = useState("");
  const [editedLong, setEditedLong] = useState("");

  const suggest = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/ai/athlete/${athleteId}/bio-suggest`, { hints });
      setResult(r.data);
      if (r.data?.proposal) {
        setEditedShort(r.data.proposal.bio_short || "");
        setEditedLong(r.data.proposal.bio_long || "");
      }
      if (r.data?.status === "ia_indisponible") toast.error("IA temporairement indisponible.");
      if (r.data?.status === "erreur") toast.error("L'IA n'a pas pu générer de proposition. Réessayez.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setLoading(false);
    }
  };

  const accept = () => {
    onApply({ bio_short: editedShort, bio_long: editedLong });
    setResult(null);
  };

  const currentShort = athlete?.bio_short || "";
  const currentLong = athlete?.bio_long || "";

  return (
    <div className="space-y-3" data-testid="ai-bio-suggester">
      <Card className="p-4">
        <Label className="text-sm font-semibold">Notes ou orientation (optionnel)</Label>
        <Textarea
          value={hints}
          onChange={(e) => setHints(e.target.value)}
          placeholder="Ex : mets l'accent sur mes titres nationaux, ou parle de mon parcours dans l'insertion des jeunes."
          rows={2}
          className="mt-2 text-sm"
          data-testid="ai-bio-hints"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">L'IA travaille uniquement à partir de ta fiche actuelle. Elle n'invente aucun fait.</p>
          <Button onClick={suggest} disabled={loading} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="ai-bio-generate">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
            {currentShort || currentLong ? "Reformuler avec l'IA" : "Générer ma bio avec l'IA"}
          </Button>
        </div>
      </Card>

      {result?.proposal && (
        <>
          <Card className="p-4" data-testid="ai-bio-diff">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold flex items-center gap-1.5">
              <Info className="h-3 w-3" /> Avant → Après
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="rounded-md border border-dashed p-3 bg-muted/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Bio actuelle</div>
                <div className="text-xs whitespace-pre-line font-mono">
                  <div className="mb-2"><span className="text-muted-foreground">Courte :</span> {currentShort || <em className="text-muted-foreground">(vide)</em>}</div>
                  <div><span className="text-muted-foreground">Longue :</span> {currentLong || <em className="text-muted-foreground">(vide)</em>}</div>
                </div>
              </div>
              <div className="rounded-md border border-[hsl(var(--aito-lagoon))]/50 p-3 bg-[hsl(var(--aito-lagoon))]/5">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--aito-lagoon))] mb-1">Proposition IA (modifiable)</div>
                <Label className="text-[10px] mt-2">Bio courte</Label>
                <Input value={editedShort} onChange={(e) => setEditedShort(e.target.value)} className="text-xs" data-testid="ai-bio-edit-short" />
                <Label className="text-[10px] mt-2">Bio longue</Label>
                <Textarea value={editedLong} onChange={(e) => setEditedLong(e.target.value)} rows={5} className="text-xs" data-testid="ai-bio-edit-long" />
              </div>
            </div>
            {result.proposal.tone_note && <p className="text-[10px] text-muted-foreground italic mt-2">Ton : {result.proposal.tone_note}</p>}
            <div className="flex items-center gap-2 mt-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setResult(null)} data-testid="ai-bio-ignore">
                <X className="h-3.5 w-3.5 mr-1" /> Ignorer
              </Button>
              <Button size="sm" onClick={accept} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="ai-bio-accept">
                <Check className="h-3.5 w-3.5 mr-1" /> Enregistrer ces bios
              </Button>
            </div>
          </Card>
          {result.context && (
            <details className="rounded-md border p-2 bg-muted/30">
              <summary className="text-[10px] cursor-pointer text-muted-foreground">Voir le contexte transmis à l'IA (transparence)</summary>
              <pre className="text-[10px] whitespace-pre-wrap mt-2 font-mono max-h-64 overflow-auto">{result.context}</pre>
            </details>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Palmarès suggester : extract from free text + dedupe against existing
// ---------------------------------------------------------------------------
function PalmaresSuggester({ athleteId, athlete, onApply }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [overrides, setOverrides] = useState({}); // { index: { discipline, result_type } }

  // Disciplines déjà connues sur la fiche (helper pour l'IA)
  const knownDisciplines = useMemo(() => {
    const set = new Set();
    if (athlete?.main_discipline) set.add(athlete.main_discipline);
    for (const d of (athlete?.disciplines || [])) set.add(d);
    return Array.from(set);
  }, [athlete]);

  const extract = async () => {
    if (!text.trim()) { toast.error("Colle du texte à analyser."); return; }
    setLoading(true);
    try {
      const r = await api.post(`/ai/athlete/${athleteId}/palmares-suggest`, { text });
      setResult(r.data);
      // pré-sélection : tout ce qui est structuré
      const s = new Set();
      const ov = {};
      (r.data?.new_entries || []).forEach((e, i) => {
        if (e.confidence === "structuré") s.add(i);
        ov[i] = {
          // Fallback discipline principale si l'IA n'en a pas trouvé
          discipline: e.discipline || athlete?.main_discipline || "",
          // result_type par défaut : "declaratif" (l'athlète confirme s'il a une preuve fédérale)
          result_type: e.result_type || "declaratif",
        };
      });
      setSelected(s);
      setOverrides(ov);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (i) => {
    const s = new Set(selected);
    if (s.has(i)) s.delete(i); else s.add(i);
    setSelected(s);
  };

  const setOverride = (i, field, value) => {
    setOverrides((o) => ({ ...o, [i]: { ...(o[i] || {}), [field]: value } }));
  };

  const acceptSelected = () => {
    const toAdd = (result.new_entries || [])
      .map((e, i) => ({ e, i }))
      .filter(({ i }) => selected.has(i))
      .map(({ e, i }) => ({
        ...e,
        // Applique les surcharges utilisateur (discipline + type de résultat)
        discipline: overrides[i]?.discipline || e.discipline || null,
        result_type: overrides[i]?.result_type || e.result_type || "declaratif",
      }));
    if (toAdd.length === 0) { toast.info("Sélectionne au moins un résultat."); return; }
    const merged = [...(athlete?.achievements || []), ...toAdd];
    onApply({ achievements: merged });
    setResult(null);
    setText("");
    setSelected(new Set());
    setOverrides({});
  };

  return (
    <div className="space-y-3" data-testid="ai-palmares-suggester">
      <Card className="p-4">
        <Label className="text-sm font-semibold">Colle un texte libre (ancien palmarès, CV, article…)</Label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ex : 2024 - Championne du monde No-Gi à Las Vegas (or); 2023 - 3e Panams; 2022 - Vice-championne d'Europe."
          rows={5}
          className="mt-2 text-sm font-mono"
          data-testid="ai-palmares-text"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">
            L'IA extrait les résultats et compare aux <strong>{(athlete?.achievements || []).length}</strong> déjà présents pour éviter les doublons.
          </p>
          <Button onClick={extract} disabled={loading || !text.trim()} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="ai-palmares-extract">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Extraire avec l'IA
          </Button>
        </div>
      </Card>

      {result && (
        <>
          <Card className="p-4" data-testid="ai-palmares-diff">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Résultats détectés</div>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="border-[hsl(var(--aito-lagoon))]/60">{result.new_entries?.length || 0} nouveaux</Badge>
                <Badge variant="secondary" className="text-muted-foreground">{result.duplicates?.length || 0} doublons</Badge>
              </div>
            </div>

            {(result.new_entries || []).length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucun nouveau résultat détecté (soit texte insuffisant, soit tous déjà présents).</p>
            ) : (
              <div className="space-y-2">
                {result.new_entries.map((e, i) => (
                  <div key={i} className={`p-2 rounded-md border transition-colors ${selected.has(i) ? "bg-[hsl(var(--aito-lagoon))]/5 border-[hsl(var(--aito-lagoon))]/40" : "bg-background"}`}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} className="mt-1 shrink-0" data-testid={`ai-palmares-check-${i}`} />
                      <div className="flex-1 min-w-0 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          {e.year && <span className="font-mono text-muted-foreground">{e.year}</span>}
                          {e.medal && <Badge className="text-[9px] uppercase" variant="secondary">{e.medal}</Badge>}
                          <span className="font-semibold">{e.title || e.competition || "—"}</span>
                          {e.confidence === "à_confirmer" && <Badge variant="outline" className="text-[9px] text-orange-600 border-orange-500/40">à confirmer</Badge>}
                        </div>
                        {(e.competition || e.level || e.category) && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {[e.competition && e.competition !== e.title ? e.competition : null, e.level, e.category].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </label>
                    {selected.has(i) && (
                      <div className="mt-2 pl-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Discipline</label>
                          <input
                            list={`disciplines-list-${i}`}
                            value={overrides[i]?.discipline || ""}
                            onChange={(ev) => setOverride(i, "discipline", ev.target.value)}
                            placeholder="ex: jiu-jitsu-bresilien"
                            className="w-full text-xs border rounded-md px-2 py-1.5 bg-background"
                            data-testid={`ai-palmares-discipline-${i}`}
                          />
                          <datalist id={`disciplines-list-${i}`}>
                            {knownDisciplines.map((d) => <option key={d} value={d} />)}
                          </datalist>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Type de résultat</label>
                          <select
                            value={overrides[i]?.result_type || "declaratif"}
                            onChange={(ev) => setOverride(i, "result_type", ev.target.value)}
                            className="w-full text-xs border rounded-md px-2 py-1.5 bg-background"
                            data-testid={`ai-palmares-result-type-${i}`}
                          >
                            <option value="declaratif">Déclaratif</option>
                            <option value="officiel_federation">Résultat officiel fédération</option>
                            <option value="a_confirmer">À confirmer</option>
                            <option value="autre">Autre</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(result.duplicates || []).length > 0 && (
              <details className="mt-3 rounded-md border p-2 bg-muted/30">
                <summary className="text-xs cursor-pointer text-muted-foreground">{result.duplicates.length} doublon(s) écarté(s)</summary>
                <ul className="text-[11px] mt-2 space-y-0.5">
                  {result.duplicates.map((d, i) => (
                    <li key={i}>{d.year || "—"} — {d.title || d.competition}</li>
                  ))}
                </ul>
              </details>
            )}

            <div className="flex items-center gap-2 mt-4 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setResult(null); setText(""); }} data-testid="ai-palmares-ignore">
                <X className="h-3.5 w-3.5 mr-1" /> Ignorer
              </Button>
              <Button size="sm" onClick={acceptSelected} disabled={selected.size === 0} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="ai-palmares-accept">
                <Check className="h-3.5 w-3.5 mr-1" /> Ajouter {selected.size} résultat(s)
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default AIAssistantPanel;
